import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  UserIcon,
  CalendarIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { AuthContext } from '../contexts/AuthContext';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';
import CreateTeamModal from '../components/CreateTeamModal';

const TeamsPage: React.FC = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [filter, setFilter] = useState<'all' | 'admin' | 'member'>('all');

  const currentUser = authContext?.user;

  // Fetch teams from Firebase
  useEffect(() => {
    if (!currentUser?.uid) return;
    
    setLoading(true);
    const teamsRef = ref(database, 'teams');
    const unsubscribe = onValue(teamsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const teamsList = Object.entries(data)
          .map(([id, team]: [string, any]) => ({ 
            id, 
            ...team,
            memberCount: team.members ? Object.keys(team.members).length : 0,
            isAdmin: team.members?.[currentUser.uid]?.role === 'admin' || team.owner === currentUser.uid
          }))
          .filter(team => team.members && Object.keys(team.members).includes(currentUser.uid));
        setTeams(teamsList);
      } else {
        setTeams([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Filter and search teams
  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || 
      (filter === 'admin' && team.isAdmin) || 
      (filter === 'member' && !team.isAdmin);
    
    return matchesSearch && matchesFilter;
  });

  const getInitials = (name: string) => {
    return name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'TM';
  };

  const getRoleBadge = (team: any) => {
    if (team.isAdmin) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          Admin
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Member
      </span>
    );
  };

  if (!authContext) {
    return <div>Loading authentication context...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Teams</h1>
              <p className="mt-2 text-gray-600">
                Manage your teams and collaborate with your colleagues
              </p>
            </div>
            <button
              onClick={() => setShowCreateTeamModal(true)}
              className="mt-4 sm:mt-0 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg shadow hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
            >
              <PlusIcon className="w-5 h-5" />
              Create New Team
            </button>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search teams..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              All Teams
            </button>
            <button
              onClick={() => setFilter('admin')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'admin' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Admin
            </button>
            <button
              onClick={() => setFilter('member')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'member' 
                  ? 'bg-gray-600 text-white' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Member
            </button>
          </div>
        </div>

        {/* Teams Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredTeams.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No teams found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filter !== 'all' 
                ? 'Try adjusting your search or filter criteria.' 
                : 'Get started by creating your first team.'}
            </p>
            {!searchTerm && filter === 'all' && (
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateTeamModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Create Team
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTeams.map((team) => (
              <div
                key={team.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/teams/${team.id}`)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-semibold text-lg">
                        {getInitials(team.name)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">{team.name}</h3>
                        {getRoleBadge(team)}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // TODO: Add team options menu
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <EllipsisVerticalIcon className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-gray-500">
                      <UserIcon className="w-4 h-4 mr-2" />
                      {team.memberCount} member{team.memberCount !== 1 ? 's' : ''}
                    </div>
                    
                    {team.created_at && (
                      <div className="flex items-center text-sm text-gray-500">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        Created {new Date(team.created_at).toLocaleDateString()}
                      </div>
                    )}
                    
                    {team.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {team.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        Last active: {team.last_activity ? new Date(team.last_activity).toLocaleDateString() : 'Never'}
                      </span>
                      <SparklesIcon className="w-4 h-4 text-blue-500" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Team Modal */}
      {showCreateTeamModal && (
        <CreateTeamModal 
          isOpen={showCreateTeamModal} 
          onClose={() => setShowCreateTeamModal(false)} 
        />
      )}

      <AppFooter />
    </div>
  );
};

export default TeamsPage; 
 